const KT_LOTTIE_ENGINE_PROMISE = '__ktLottieEnginePromise';

function loadLottieEngine(url) {
  if (window.lottie?.loadAnimation) return Promise.resolve(window.lottie);
  if (window[KT_LOTTIE_ENGINE_PROMISE]) return window[KT_LOTTIE_ENGINE_PROMISE];

  window[KT_LOTTIE_ENGINE_PROMISE] = new Promise((resolve, reject) => {
    let script = Array.from(document.scripts).find(
      (candidate) => candidate.dataset.ktLottieEngine === 'true'
    );

    const handleLoad = () => {
      if (window.lottie?.loadAnimation) resolve(window.lottie);
      else reject(new Error('Lottie engine unavailable'));
    };

    const handleError = () => reject(new Error('Lottie engine failed to load'));

    if (!script) {
      script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.dataset.ktLottieEngine = 'true';
      document.head.appendChild(script);
    }

    if (window.lottie?.loadAnimation) {
      resolve(window.lottie);
      return;
    }

    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });
  }).catch((error) => {
    window[KT_LOTTIE_ENGINE_PROMISE] = null;
    throw error;
  });

  return window[KT_LOTTIE_ENGINE_PROMISE];
}

if (!customElements.get('kt-lottie-player')) {
  class KTLottiePlayer extends HTMLElement {
    connectedCallback() {
      if (this.isConnectedToPlayer) return;
      this.isConnectedToPlayer = true;
      this.trigger = this.querySelector('[data-kt-lottie-trigger]');
      this.stage = this.querySelector('[data-kt-lottie-stage]');
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.pointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.handleMouseEnter = () => {
        if (this.pointerQuery.matches) this.requestPlay();
      };
      this.handleFocus = () => {
        if (this.pointerQuery.matches) this.requestPlay();
      };
      this.handleClick = () => this.requestPlay();
      this.handleMotionChange = () => this.syncMotionPreference();

      this.trigger?.addEventListener('mouseenter', this.handleMouseEnter);
      this.trigger?.addEventListener('focus', this.handleFocus);
      this.trigger?.addEventListener('click', this.handleClick);
      this.motionQuery.addEventListener('change', this.handleMotionChange);
      this.syncMotionPreference();
    }

    disconnectedCallback() {
      this.prepareObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.animation?.destroy();
      this.animation = null;
      this.trigger?.removeEventListener('mouseenter', this.handleMouseEnter);
      this.trigger?.removeEventListener('focus', this.handleFocus);
      this.trigger?.removeEventListener('click', this.handleClick);
      this.motionQuery?.removeEventListener('change', this.handleMotionChange);
      this.isConnectedToPlayer = false;
    }

    syncMotionPreference() {
      const reduceMotion = this.motionQuery.matches;
      this.toggleAttribute('data-reduced-motion', reduceMotion);
      if (this.trigger) {
        this.trigger.disabled = reduceMotion;
        this.trigger.setAttribute('aria-hidden', reduceMotion ? 'true' : 'false');
      }

      this.prepareObserver?.disconnect();
      this.visibilityObserver?.disconnect();

      if (reduceMotion) {
        this.animation?.destroy();
        this.animation = null;
        this.preparePromise = null;
        this.isReady = false;
        this.classList.remove('is-active');
        if (this.stage) this.stage.replaceChildren();
        this.dataset.state = 'poster';
        return;
      }

      this.observeApproach();
      this.observeMobileVisibility();
    }

    observeApproach() {
      this.prepareObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.prepareObserver.disconnect();
          this.prepare();
        },
        { rootMargin: '300px 0px', threshold: 0.01 }
      );
      this.prepareObserver.observe(this);
    }

    observeMobileVisibility() {
      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          const visible = entries.some(
            (entry) => entry.isIntersecting && entry.intersectionRatio >= 0.55
          );
          if (!visible || this.pointerQuery.matches || this.hasPlayed) return;
          this.visibilityObserver.disconnect();
          this.requestPlay();
        },
        { threshold: [0.55] }
      );
      this.visibilityObserver.observe(this);
    }

    prepare() {
      if (this.motionQuery.matches) return Promise.resolve(null);
      if (this.isReady) return Promise.resolve(this.animation);
      if (this.preparePromise) return this.preparePromise;

      this.dataset.state = 'loading';
      this.preparePromise = loadLottieEngine(this.dataset.engineUrl)
        .then((lottie) => {
          if (!this.isConnected) return null;

          return new Promise((resolve, reject) => {
            const animation = lottie.loadAnimation({
              container: this.stage,
              renderer: 'svg',
              loop: false,
              autoplay: false,
              path: this.dataset.animationUrl,
              rendererSettings: {
                preserveAspectRatio: 'xMidYMid meet',
                progressiveLoad: true,
                hideOnTransparent: true
              }
            });

            this.animation = animation;
            animation.addEventListener('DOMLoaded', () => {
              this.isReady = true;
              this.dataset.state = 'ready';
              resolve(animation);
            });
            animation.addEventListener('data_failed', () => reject(new Error('Lottie data failed')));
            animation.addEventListener('error', () => reject(new Error('Lottie render failed')));
            animation.addEventListener('complete', () => {
              this.dataset.state = 'complete';
              this.classList.remove('is-playing');
            });
          });
        })
        .catch(() => {
          this.failSafely();
          return null;
        });

      return this.preparePromise;
    }

    requestPlay() {
      if (this.motionQuery.matches) return;
      const now = performance.now();
      if (this.lastPlayRequest && now - this.lastPlayRequest < 120) return;
      this.lastPlayRequest = now;

      this.prepare().then((animation) => {
        if (!animation || !this.isConnected) return;
        this.classList.add('is-active', 'is-playing');
        this.dataset.state = 'playing';
        this.hasPlayed = true;
        animation.goToAndPlay(0, true);
      });
    }

    failSafely() {
      this.animation?.destroy();
      this.animation = null;
      this.preparePromise = null;
      this.isReady = false;
      this.classList.remove('is-active', 'is-playing');
      if (this.stage) this.stage.replaceChildren();
      this.dataset.state = 'error';
    }
  }

  customElements.define('kt-lottie-player', KTLottiePlayer);
}
